import {Button, Card, Col, Row} from 'antd';
import {CardProps} from 'antd/es/card';
import {
  Acquirer,
  PaymentMethods,
  Processor,
  ProviderFieldsFragment,
  useAccountQuery,
  useProviderQuery
} from 'api/generated';
import {Ellipsize, PageHeader, PropertyList, Stack} from 'components';
import {formatBoolean, formatIntAmount} from 'lib/formaters';
import {acquirerLabels, paymentMethodLabels, paymentProcessorLabels} from 'lib/labels';
import React from 'react';
import {Link, RouteComponentProps, useHistory} from 'react-router-dom';
import {ProviderMetadataList} from './ProviderMetadtaList';
import {ProviderStatusTags} from './ProviderStatusTag';
import {ProviderTimeline} from './ProviderTimeline';

type Props = {providerId: string; accountId: string};

export const Provider: React.FC<RouteComponentProps<Props>> = ({match}) => {
  const {providerId, accountId} = match.params;
  const history = useHistory();

  const {data: accountData, loading: loadingAccount} = useAccountQuery({
    variables: {id: accountId}
  });
  const account = accountData?.account;

  const {data: providerData, loading: loadingProvider} = useProviderQuery({
    variables: {id: providerId}
  });

  const provider = providerData?.provider as ProviderFieldsFragment;
  const cardProps: CardProps = {
    style: {marginBottom: 20},
    loading: loadingProvider,
    size: 'small'
  };
  const isLoading = loadingProvider || loadingAccount;
  const title = provider
    ? `${acquirerLabels[provider?.acquirer as Acquirer]} - ${
        paymentMethodLabels[provider?.paymentMethod as PaymentMethods]
      }`
    : account?.name || account?.business?.companyName || account?.business?.legalName || '';

  const handleClickEdit = () => history.push(`/accounts/${accountId}/providers/${providerId}/edit`);

  return (
    <Row>
      <Col span={24} offset={0}>
        <PageHeader
          loading={isLoading}
          backPath={`/accounts/${accountId}?tab=payment-methods`}
          title={
            <Stack gutter={10}>
              <Ellipsize>{title}</Ellipsize>
              <ProviderStatusTags provider={provider} />
            </Stack>
          }>
          <Button onClick={handleClickEdit}>Update</Button>
        </PageHeader>
        <Row gutter={20}>
          <Col span={16}>
            <Card title="Settings" {...cardProps}>
              <PropertyList
                omitEmpty
                loading={loadingProvider}
                labelWidth={500}
                properties={[
                  {
                    label: 'ID',
                    copyable: true,
                    value: provider?.id
                  },
                  {
                    label: 'Account ID',
                    copyable: {text: account?.id},
                    value: <Link to={`/accounts/${accountId}`}>{account?.id}</Link>
                  },
                  {
                    label: 'Payment method',
                    value: paymentMethodLabels[provider?.paymentMethod as PaymentMethods]
                  },
                  {
                    label: 'Payment processor',
                    value: paymentProcessorLabels[provider?.processor as Processor]
                  },
                  {
                    label: 'Acquirer',
                    value: acquirerLabels[provider?.acquirer as Acquirer]
                  },
                  {
                    label: "Provider's native payment page",
                    value: formatBoolean(provider?.nativeHostedPayment, 'Yes', 'No')
                  },
                  {
                    label: 'Enabled',
                    value: formatBoolean(provider?.enabled, 'Yes', 'No')
                  },
                  {
                    label: 'TEST Mode',
                    value: formatBoolean(provider?.test, 'Yes', 'No')
                  }
                ]}
              />
            </Card>
            <Card title="Payment method settings" {...cardProps}>
              <ProviderMetadataList provider={provider} loading={loadingProvider} />
            </Card>
            {provider?.rules && (
              <Card title="Routing rules" {...cardProps}>
                <PropertyList
                  omitEmpty
                  loading={loadingProvider}
                  labelWidth={500}
                  properties={[
                    {
                      label: 'Minimum payment amount',
                      value: formatIntAmount(provider?.rules.minAmount)
                    },
                    {
                      label: 'Maximum payment amount',
                      value: formatIntAmount(provider?.rules.maxAmount)
                    },
                    {
                      label: 'The name of the store matches',
                      value: provider?.rules.shopNameLike
                    },
                    {
                      label: 'The payment description matches',
                      value: provider?.rules.descriptionLike
                    }
                  ]}
                />
              </Card>
            )}
          </Col>
          <Col span={8}>
            <ProviderTimeline />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
